import React, { useState, useEffect, useCallback } from "react";
import SafeAnimatePresence from "../../components/ui/SafeAnimatePresence";
import {
  FiMic,
  FiPhone,
  FiUsers,
  FiFileText,
  FiRefreshCw,
  FiSearch,
  FiPlus,
  FiShoppingBag,
  FiCheck,
  FiX,
} from "react-icons/fi";
import {
  getVoiceAgentsDF,
  getPhoneNumbersDF,
  getCallHistoryDF,
  redirectToToolbox,
} from "../../services/voiceDataFabricService";
import type { VoiceAgent, PhoneNumber, CallLog } from "./voice.types";
import { mapDfAgent, mapDfPhone, mapDfCall } from "./voice.data";
import AgentsTab from "./components/AgentsTab";
import PhoneNumbersTab from "./components/PhoneNumbersTab";
import CallLogsTab from "./components/CallLogsTab";
import { useCallDetailPanel } from "../../context/CallDetailPanelContext";
import "../../styles/Voice.css";
import "../../styles/MomentumCommand.css";

type Tab = "agents" | "my-numbers" | "buy-numbers" | "call-logs";

const VoiceDashboard: React.FC = () => {
  const [tab, setTab] = useState<Tab>("agents");

  /* ── Data state ── */
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [phones, setPhones] = useState<PhoneNumber[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ── Agent search (shown in hero row when Agents tab is active) ── */
  const [agentSearch, setAgentSearch] = useState("");
  const [agentPanelTitle, setAgentPanelTitle] = useState<string | null>(null);
  const [phoneMySearch, setPhoneMySearch] = useState("");
  const [phoneBuySearch, setPhoneBuySearch] = useState("");
  const [callSearch, setCallSearch] = useState("");
  const [globalToast, setGlobalToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setGlobalToast({ msg, type });
    setTimeout(() => setGlobalToast(null), 3500);
  }, []);

  const { setCallDetailPanel } = useCallDetailPanel();

  /* ── Load data from DataFabric (Toolbox company) ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dfAgents, dfPhones, dfCalls] = await Promise.all([
        getVoiceAgentsDF(),
        getPhoneNumbersDF(),
        getCallHistoryDF(),
      ]);

      const mappedAgents = dfAgents.map((a, i) => mapDfAgent(a, i));
      const agentNames: Record<string, string> = {};
      mappedAgents.forEach((a) => {
        if (a.elevenLabsAgentId) agentNames[a.elevenLabsAgentId] = a.name;
      });

      setAgents(mappedAgents);
      setPhones(dfPhones.map(mapDfPhone));
      setCalls(dfCalls.map((c, i) => mapDfCall(c, i, agentNames)));
    } catch (e: any) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Loading skeleton ── */
  if (loading)
    return (
      <div className="voice-page">
        {/* Hero skeleton */}
        <div className="voice-page__hero" style={{ gap: "var(--space-2)" }}>
          <div className="voice-skeleton voice-skeleton--md" style={{ width: 160 }} />
          <div style={{ flex: 1 }} />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="voice-skeleton voice-skeleton--md" style={{ width: 100, borderRadius: "var(--radius-lg)" }} />
          ))}
          <div className="voice-skeleton voice-skeleton--md" style={{ width: 36, borderRadius: "var(--radius-lg)" }} />
        </div>

        {/* Agents tab skeleton — card grid */}
        {tab === "agents" && (
          <div className="voice-agents-grid" style={{ padding: "var(--space-4)" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="vac" style={{ gap: "var(--space-3)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                  <div className="voice-skeleton" style={{ width: 44, height: 44, borderRadius: "var(--radius-lg)", flexShrink: 0 }} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div className="voice-skeleton voice-skeleton--md" style={{ width: "60%" }} />
                    <div className="voice-skeleton voice-skeleton--sm" style={{ width: "40%" }} />
                  </div>
                </div>
                <div className="voice-skeleton voice-skeleton--sm" style={{ width: "30%" }} />
              </div>
            ))}
          </div>
        )}

        {/* Call Logs skeleton — table rows */}
        {tab === "call-logs" && (
          <div style={{ padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cc-sb-split-card" style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <div className="voice-skeleton" style={{ width: 36, height: 36, borderRadius: "var(--radius-lg)", flexShrink: 0 }} />
                <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div className="voice-skeleton voice-skeleton--md" style={{ width: "50%" }} />
                  <div className="voice-skeleton voice-skeleton--sm" style={{ width: "35%" }} />
                </div>
                <div className="voice-skeleton voice-skeleton--sm" style={{ width: 60 }} />
                <div className="voice-skeleton voice-skeleton--sm" style={{ width: 70 }} />
                <div className="voice-skeleton voice-skeleton--md" style={{ width: 50, borderRadius: "var(--radius-lg)" }} />
              </div>
            ))}
          </div>
        )}

        {/* Phone Numbers skeleton — table rows */}
        {(tab === "my-numbers" || tab === "buy-numbers") && (
          <div style={{ padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="cc-sb-split-card" style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <div className="voice-skeleton" style={{ width: 36, height: 36, borderRadius: "var(--radius-lg)", flexShrink: 0 }} />
                <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div className="voice-skeleton voice-skeleton--md" style={{ width: "45%" }} />
                  <div className="voice-skeleton voice-skeleton--sm" style={{ width: "30%" }} />
                </div>
                <div className="voice-skeleton voice-skeleton--sm" style={{ width: 80 }} />
                <div className="voice-skeleton voice-skeleton--md" style={{ width: 60, borderRadius: "var(--radius-lg)" }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );

  /* ── Error state ── */
  if (error)
    return (
      <div className="voice-page">
        <div className="voice-empty voice-dashboard__empty--full">
          <div className="voice-empty__icon">
            <FiMic size={28} />
          </div>
          <div className="voice-empty__title">Could not load data</div>
          <div className="voice-empty__desc">{error}</div>
          <button className="glass-btn glass-btn-primary" onClick={loadData}>
            <FiRefreshCw size={13} /> Retry
          </button>
        </div>
      </div>
    );

  return (
    <div className="voice-page">
      {/* Hero */}
      <div className="voice-page__hero">
        {/* Call Logs search */}
        {tab === "call-logs" && (
          <div className="voice-search">
            <FiSearch size={13} className="voice-search__icon" />
            <input
              className="voice-search__input"
              placeholder="Search calls..."
              value={callSearch}
              onChange={(e) => setCallSearch(e.target.value)}
            />
          </div>
        )}

        {/* Phone Numbers: search in hero row */}
        {(tab === "my-numbers" || tab === "buy-numbers") && (
          <div className="voice-search">
            <FiSearch size={13} className="voice-search__icon" />
            <input
              className="voice-search__input"
              placeholder={tab === "my-numbers" ? "Search My Numbers..." : "Search by Area Code, City, State..."}
              value={tab === "my-numbers" ? phoneMySearch : phoneBuySearch}
              onChange={(e) => tab === "my-numbers" ? setPhoneMySearch(e.target.value) : setPhoneBuySearch(e.target.value)}
            />
          </div>
        )}


        {/* Left: title when panel open, search when grid */}
        {tab === "agents" && agentPanelTitle && (
          <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)" }}>
            {agentPanelTitle}
          </div>
        )}
        {tab === "agents" && !agentPanelTitle && (
          <div className="voice-search">
            <FiSearch size={13} className="voice-search__icon" />
            <input
              className="voice-search__input"
              placeholder="Search agents..."
              value={agentSearch}
              onChange={(e) => setAgentSearch(e.target.value)}
            />
          </div>
        )}
        <div style={{ flex: 1 }} />
        <div className="voice-page__hero-actions">
          {(
            [
              {
                key: "agents",
                label: "Agents",
                icon: <FiUsers size={13} />,
                count: agents.length,
              },
              {
                key: "call-logs",
                label: "Call Logs",
                icon: <FiFileText size={13} />,
                count: calls.length,
              },
              {
                key: "my-numbers",
                label: "My Phone Numbers",
                icon: <FiPhone size={13} />,
                count: phones.length,
              },
              {
                key: "buy-numbers",
                label: "Buy Phone Numbers",
                icon: <FiShoppingBag size={13} />,
              },
            ] as {
              key: Tab;
              label: string;
              icon: React.ReactNode;
              count?: number;
            }[]
          ).map((t) => (
            <button
              key={t.key}
              className="dw-list__new-btn"
              style={tab !== t.key ? { background: "var(--surface-glass)", color: "var(--text-secondary)", border: "1px solid var(--border-glass)" } : {}}
              onClick={() => { setTab(t.key); setCallDetailPanel(null); setAgentPanelTitle(null); }}
            >
              {t.icon} {t.label}
              {t.count !== undefined && (
                <span className="dw-list__count">{t.count}</span>
              )}
            </button>
          ))}
          <button
            className="mc-btn-icon"
            onClick={loadData}
            title="Refresh"
          >
            <FiRefreshCw size={13} />
          </button>
          {tab === "agents" && !agentPanelTitle && (
            <button
              className="dw-list__new-btn voice-dashboard__new-agent-btn"
              title="New Agent"
              onClick={() => {
                (document.getElementById("voice-new-agent-trigger") as HTMLButtonElement)?.click();
              }}
            >
              <FiPlus size={13} />
            </button>
          )}
        </div>
      </div>

      <SafeAnimatePresence mode="wait">
        {tab === "agents" && (
          <AgentsTab
            key="agents"
            agents={agents}
            phones={phones}
            search={agentSearch}
            onSearchChange={setAgentSearch}
            onEdit={redirectToToolbox}
            onCreate={redirectToToolbox}
            onAgentCreated={loadData}
            onDeleteSuccess={(name) => showToast(`"${name}" deleted successfully`, 'success')}
            onPanelChange={setAgentPanelTitle}
          />
        )}
        {(tab === "my-numbers" || tab === "buy-numbers") && (
          <PhoneNumbersTab
            key="phones"
            phones={phones}
            agents={agents}
            onPurchased={loadData}
            subTab={tab === "buy-numbers" ? "buy" : "my"}
            onSubTabChange={(t) => setTab(t === "buy" ? "buy-numbers" : "my-numbers")}
            mySearch={phoneMySearch}
            buySearch={phoneBuySearch}
          />
        )}
        {tab === "call-logs" && (
          <CallLogsTab
            key="calls"
            calls={calls}
            agents={agents}
            loading={loading}
            onCallClick={(c) => setCallDetailPanel({ call: c, agents })}
            search={callSearch}
            onSearchChange={setCallSearch}
          />
        )}
      </SafeAnimatePresence>

      {/* ── Global toast ── */}
      {globalToast && (
        <div style={{
          position: 'fixed', top: 16, right: 20, zIndex: 10050,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', borderRadius: 10,
          background: globalToast.type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${globalToast.type === 'success' ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
          color: globalToast.type === 'success' ? '#10b981' : '#f87171',
          fontSize: 13, fontWeight: 500,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          minWidth: 240, maxWidth: 380,
        }}>
          {globalToast.type === 'success' ? <FiCheck size={14} /> : <FiX size={14} />}
          <span style={{ flex: 1 }}>{globalToast.msg}</span>
          <button onClick={() => setGlobalToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1, opacity: 0.7 }}>
            <FiX size={13} />
          </button>
        </div>
      )}

    </div>
  );
};

export default VoiceDashboard;